"use client";
import "../styles/index.scss";
import { loadIcons } from "@/utils/icon-loader";
import React from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";
import AppHeader from "@/components/AppHeader/AppHeader";
import AppFooter from "@/components/AppFooter/AppFooter";
import ReduxProvider from "@/providers/ReduxProvider";
import { ModalProvider } from "@/context/ModalContext";
import "bootstrap/dist/css/bootstrap.min.css";
import "./globals.css";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import AuthProvider from "@/providers/AuthProvider";
import { uhcsans } from "./font";
import Head from "next/head";
import ErrorDisplay from "@/components/Common/ErrorDisplay/ErrorDisplay";
import GlobalNotifications from "@/components/Common/GlobalNotifications/GlobalNotifications";
import CookieBanner from "@/components/CookieBanner/CookieBanner";

interface LayoutProps {
  children: React.ReactNode;
}

const App: React.FC<LayoutProps> = ({ children }) => {
  loadIcons();
  // Small wrappers that live inside the ReduxProvider so they can safely
  // use react-redux hooks to read state and decide whether to render the
  // app frame (header/footer). We intentionally check `user?.type` to
  // hide these elements for internal users.
  const HeaderWrapper: React.FC = () => {
    const user = useSelector((state: RootState) => state.user?.user);
    if (user?.type === "internal") return null;
    return <AppHeader />;
  };

  const FooterWrapper: React.FC = () => {
    const user = useSelector((state: RootState) => state.user?.user);
    if (user?.type === "internal") return null;
    return <AppFooter />;
  };
  return (
    <html lang="en" className={`${uhcsans.className} antialiased`}>
      <Head>
        <title>UnitedHealth Premium</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content="UnitedHealth Premium Application" />
      </Head>
      <body>
        <ReduxProvider>
          <ModalProvider>
            <AuthProvider>
              <HeaderWrapper />
              <ErrorDisplay />
              <GlobalNotifications />
              <main className="min-h-[90vh]">{children}</main>
              <FooterWrapper />
              <CookieBanner />
            </AuthProvider>
          </ModalProvider>
        </ReduxProvider>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </body>
    </html>
  );
};

export default App;
